import { DocumentData, doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { ADMIN_ANALYTICS_COLLECTION, ADMIN_ANALYTICS_DOCS } from "./collectionNames";
import { dateField, logDevelopmentError, stringField } from "./firestoreHelpers";

export type FounderBriefStatus = "growing" | "healthy" | "watch" | "action-needed";

export type FounderBriefStory = {
  id: string;
  icon: string;
  text: string;
  significance: number;
};

export type FounderBriefHealthCard = {
  id: "community" | "revenue" | "growth" | "infrastructure" | "needs-attention";
  icon: string;
  title: string;
  status: FounderBriefStatus;
  statusLabel: string;
  value: string;
  sentence: string;
  to: string;
};

export type FounderBriefInsight = {
  id: string;
  icon: string;
  headline: string;
  insight: string;
  to: string;
  action: string;
  significance: number;
};

export type FounderBriefAction = {
  id: string;
  label: string;
  detail: string;
  status: FounderBriefStatus;
  to: string;
};

export type FounderBriefTimelineEvent = {
  id: string;
  icon: string;
  title: string;
  detail: string;
  occurredAt: Date;
};

export type FounderBriefReflection = {
  text: string;
  source: string;
  activity: "milestone" | "learning" | "teaching" | "community" | "opportunity" | "growth" | "quiet";
};

export type FounderBrief = {
  greeting: string;
  dateLabel: string;
  pulse: {
    status: FounderBriefStatus;
    statusLabel: string;
    summary: string;
  };
  stories: FounderBriefStory[];
  healthCards: FounderBriefHealthCard[];
  insights: FounderBriefInsight[];
  actions: FounderBriefAction[];
  timeline: FounderBriefTimelineEvent[];
  reflection: FounderBriefReflection;
  isQuiet: boolean;
  lastUpdated: Date;
};

type CachedBriefDocs = {
  userGrowth: DocumentData;
  retention: DocumentData;
  revenueToday: DocumentData;
  revenueMonth: DocumentData;
  platformHealth: DocumentData;
  coachEarnings: DocumentData;
};

const CACHED_DOC_SOURCES = [
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}`,
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.retention}`,
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.revenueToday}`,
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.revenueMonth}`,
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.platformHealthMetrics}`,
  `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.coachEarningsSummaries}`
];

export function founderBriefCachedSources() {
  return CACHED_DOC_SOURCES.join(", ");
}

export function statusLabel(status: FounderBriefStatus) {
  switch (status) {
    case "growing":
      return "Growing";
    case "healthy":
      return "Healthy";
    case "watch":
      return "Watch";
    case "action-needed":
      return "Action Needed";
  }
}

export function founderGreeting(now = new Date()) {
  const hour = now.getHours();
  if (hour < 12) return "Good morning, Melissa";
  if (hour < 17) return "Good afternoon, Melissa";
  return "Good evening, Melissa";
}

export function founderBriefDateLabel(now = new Date()) {
  const weekday = new Intl.DateTimeFormat(undefined, { weekday: "long" }).format(now);
  const date = new Intl.DateTimeFormat(undefined, { month: "long", day: "numeric" }).format(now);
  return `${weekday} • ${date}`;
}

function numberValue(data: DocumentData, keys: string[], fallback = 0) {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value.replace(/[$,%\s,]/g, ""));
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return fallback;
}

function percentValue(data: DocumentData, keys: string[], fallback = 0) {
  const value = numberValue(data, keys, fallback);
  return value > 1 ? value / 100 : value;
}

function textValue(data: DocumentData, keys: string[], fallback: string) {
  for (const key of keys) {
    const value = stringField(data, key);
    if (value) return value;
  }
  return fallback;
}

function formatCount(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function formatMoney(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: value >= 100 ? 0 : 2
  }).format(value);
}

function formatPercent(value: number) {
  return new Intl.NumberFormat(undefined, {
    style: "percent",
    maximumFractionDigits: 1
  }).format(value);
}

function plural(value: number, singular: string, pluralLabel = `${singular}s`) {
  return `${formatCount(value)} ${value === 1 ? singular : pluralLabel}`;
}

function booleanValue(data: DocumentData, keys: string[]) {
  return keys.some((key) => data[key] === true);
}

function limitReflectionToTwoSentences(text: string) {
  const sentences = text.trim().match(/[^.!?]+[.!?]+|[^.!?]+$/g) ?? [];
  return sentences.slice(0, 2).join(" ");
}

function cachedTimelineEvents(data: DocumentData): FounderBriefTimelineEvent[] {
  const rawEvents = data.timelineEvents ?? data.milestones ?? [];
  if (!Array.isArray(rawEvents)) return [];

  return rawEvents
    .filter((event): event is DocumentData => event && typeof event === "object" && !Array.isArray(event))
    .map((event, index) => ({
      id: stringField(event, "id") ?? `cached-timeline-${index}`,
      icon: stringField(event, "icon") ?? "✨",
      title: textValue(event, ["title", "headline"], "Chor milestone recorded."),
      detail: textValue(event, ["detail", "sentence", "description"], "A meaningful company moment was captured."),
      occurredAt: dateField(event, "occurredAt") ?? dateField(event, "createdAt") ?? dateField(event, "timestamp") ?? new Date(0)
    }))
    .sort((left, right) => right.occurredAt.getTime() - left.occurredAt.getTime())
    .slice(0, 6);
}

function createStory(id: string, icon: string, text: string, value: number, significance: number): FounderBriefStory | null {
  if (value <= 0) return null;
  return { id, icon, text, significance };
}

function dailyStories(docs: CachedBriefDocs): FounderBriefStory[] {
  const newDancers = numberValue(docs.userGrowth, ["newDancersToday", "dancersToday", "newDancers", "newUsersToday"]);
  const newCoaches = numberValue(docs.userGrowth, ["newCoachesToday", "coachesToday", "newCoaches"]);
  const newStudios = numberValue(docs.userGrowth, ["newStudiosToday", "studiosToday", "newStudios"]);
  const bookings = numberValue(docs.userGrowth, ["bookingsToday", "dailyBookings"]) || numberValue(docs.revenueToday, ["bookingsToday", "bookingCount"]);
  const submissions = numberValue(docs.userGrowth, ["challengeSubmissionsToday", "submissionsToday"]);
  const revenue = numberValue(docs.revenueToday, ["bookingsRevenueUSD", "grossBookingsUSD", "grossRevenueUSD", "revenueUSD", "value"]);
  const dauChange = percentValue(docs.userGrowth, ["dailyActiveUserChange", "dauChangePercent", "growthRate"]);

  const stories = [
    createStory("new-dancers", "✨", `${plural(newDancers, "new dancer")} joined Chor.`, newDancers, newDancers * 9),
    createStory("coaches-published", "❤️", `${plural(newCoaches, "coach", "coaches")} joined the coaching community.`, newCoaches, newCoaches * 8),
    createStory("bookings", "🎯", `${plural(bookings, "private lesson")} booked.`, bookings, bookings * 10),
    createStory("challenge-submissions", "🏆", `${plural(submissions, "Challenge submission")} came in.`, submissions, submissions * 7),
    createStory("bookings-revenue", "💰", `${formatMoney(revenue)} in bookings.`, revenue, revenue / 10),
    createStory("studios-joined", "🏢", `${plural(newStudios, "new studio")} joined.`, newStudios, newStudios * 8),
    createStory("active-user-growth", "📈", `Daily active users increased ${formatPercent(dauChange)}.`, dauChange, dauChange * 100)
  ].filter((story): story is FounderBriefStory => story !== null);

  return stories.sort((left, right) => right.significance - left.significance).slice(0, 5);
}

function attentionCount(docs: CachedBriefDocs) {
  return (
    numberValue(docs.platformHealth, ["failedPayouts", "payoutFailures"]) +
    numberValue(docs.platformHealth, ["failedPayments", "paymentIssues"]) +
    numberValue(docs.platformHealth, ["mediaProcessingBacklog", "mediaBacklog"]) +
    numberValue(docs.platformHealth, ["moderationReports", "openModerationReports"]) +
    numberValue(docs.platformHealth, ["stripeVerificationPending", "stripeVerificationIssues"])
  );
}

function pulseFor(docs: CachedBriefDocs, stories: FounderBriefStory[]) {
  const actions = attentionCount(docs);
  const uptime = percentValue(docs.platformHealth, ["uptimePercent", "uptimeRate", "availabilityPercent", "availabilityRate"], 1);

  if (actions > 0) {
    return {
      status: "action-needed" as const,
      statusLabel: statusLabel("action-needed"),
      summary: actions === 1 ? "One issue requires review today." : `${formatCount(actions)} issues require review today.`
    };
  }

  if (uptime > 0 && uptime < 0.99) {
    return {
      status: "watch" as const,
      statusLabel: statusLabel("watch"),
      summary: "Product health needs a closer look today."
    };
  }

  if (stories.length > 0) {
    return {
      status: "growing" as const,
      statusLabel: statusLabel("growing"),
      summary: "Chor is moving forward today."
    };
  }

  return {
    status: "healthy" as const,
    statusLabel: statusLabel("healthy"),
    summary: "Everything looks healthy today."
  };
}

function healthCards(docs: CachedBriefDocs): FounderBriefHealthCard[] {
  const activeDancers = numberValue(docs.userGrowth, ["activeDancers", "dailyActiveDancers", "dancersToday"]);
  const bookingsRevenue = numberValue(docs.revenueToday, ["bookingsRevenueUSD", "grossBookingsUSD", "grossRevenueUSD", "revenueUSD", "value"]);
  const newUsers = numberValue(docs.userGrowth, ["newUsersToday", "newUsers", "newDancersToday"]);
  const uptime = percentValue(docs.platformHealth, ["uptimePercent", "uptimeRate", "availabilityPercent", "availabilityRate"], 1);
  const needsAttention = attentionCount(docs);

  return [
    {
      id: "community",
      icon: "❤️",
      title: "Community",
      status: activeDancers > 0 ? "healthy" : "watch",
      statusLabel: statusLabel(activeDancers > 0 ? "healthy" : "watch"),
      value: activeDancers > 0 ? formatCount(activeDancers) : "Quiet",
      sentence: activeDancers > 0 ? "Dancers are active in Chor today." : "Community activity is quiet right now.",
      to: "/community"
    },
    {
      id: "revenue",
      icon: "💰",
      title: "Revenue",
      status: bookingsRevenue > 0 ? "healthy" : "watch",
      statusLabel: statusLabel(bookingsRevenue > 0 ? "healthy" : "watch"),
      value: bookingsRevenue > 0 ? formatMoney(bookingsRevenue) : "$0",
      sentence: bookingsRevenue > 0 ? "Booking money moved today." : "No booking revenue has posted today.",
      to: "/founder-finance"
    },
    {
      id: "growth",
      icon: "📈",
      title: "Growth",
      status: newUsers > 0 ? "growing" : "watch",
      statusLabel: statusLabel(newUsers > 0 ? "growing" : "watch"),
      value: newUsers > 0 ? formatCount(newUsers) : "Quiet",
      sentence: newUsers > 0 ? "New members are finding Chor." : "No new member growth has posted yet today.",
      to: "/growth"
    },
    {
      id: "infrastructure",
      icon: "🛠",
      title: "Infrastructure",
      status: uptime >= 0.99 ? "healthy" : "watch",
      statusLabel: statusLabel(uptime >= 0.99 ? "healthy" : "watch"),
      value: uptime > 0 ? formatPercent(uptime) : "Unknown",
      sentence: uptime >= 0.99 ? "Core systems are operating normally." : "Infrastructure needs monitoring.",
      to: "/system-health"
    },
    {
      id: "needs-attention",
      icon: "⚠",
      title: "Needs Attention",
      status: needsAttention > 0 ? "action-needed" : "healthy",
      statusLabel: statusLabel(needsAttention > 0 ? "action-needed" : "healthy"),
      value: needsAttention > 0 ? formatCount(needsAttention) : "None",
      sentence: needsAttention > 0 ? "Actionable issues need review." : "No action needed today.",
      to: "/product-quality"
    }
  ];
}

function insights(docs: CachedBriefDocs, stories: FounderBriefStory[]): FounderBriefInsight[] {
  const returningUsers = numberValue(docs.retention, ["returningUsersToday", "returningUsers", "activeReturningUsers"]);
  const challengeLoves = numberValue(docs.userGrowth, ["topChallengeLoves", "challengeLoves", "lovesToday"]);
  const studioGrowth = percentValue(docs.userGrowth, ["studioGrowthPercent", "studioRegistrationsChange"]);
  const coachGrowth = percentValue(docs.userGrowth, ["coachGrowthPercent", "coachRegistrationsChange"]);
  const revenue = numberValue(docs.revenueToday, ["bookingsRevenueUSD", "grossBookingsUSD", "grossRevenueUSD", "revenueUSD", "value"]);
  const topStory = stories[0];

  const ranked = [
    {
      id: "coach-growth",
      icon: "📈",
      headline: coachGrowth > 0 ? "Coach growth accelerated this week." : "Growth is ready to build.",
      insight: coachGrowth > 0 ? `Coach registrations increased ${formatPercent(coachGrowth)}.` : "The next new coach will become part of tomorrow's story.",
      to: "/growth",
      action: "View growth",
      significance: coachGrowth * 100
    },
    {
      id: "community-returning",
      icon: "❤️",
      headline: returningUsers > 0 ? `${plural(returningUsers, "dancer")} returned today.` : "Community relationships are forming.",
      insight: returningUsers > 0 ? "Returning dancers show Chor is becoming part of people's routine." : "We'll highlight returning dancers as the community grows.",
      to: "/community",
      action: "View community",
      significance: returningUsers * 6
    },
    {
      id: "studio-growth",
      icon: "🏢",
      headline: studioGrowth > 0 ? "Studio registrations increased." : "Studios are ready for their next moment.",
      insight: studioGrowth > 0 ? `Studios grew ${formatPercent(studioGrowth)}.` : "Studio momentum will appear here when it becomes meaningful.",
      to: "/growth",
      action: "View studios",
      significance: studioGrowth * 100
    },
    {
      id: "challenge-love",
      icon: "🏆",
      headline: challengeLoves > 0 ? `A Challenge reached ${formatCount(challengeLoves)} Loves.` : "Challenge energy is waiting for a spark.",
      insight: challengeLoves > 0 ? "Community appreciation is showing up around performance." : "When a Challenge catches momentum, it will rise into the brief.",
      to: "/community",
      action: "View challenges",
      significance: challengeLoves * 4
    },
    {
      id: "business",
      icon: "💰",
      headline: revenue > 0 ? "Bookings moved the business today." : "Business activity is quiet today.",
      insight: revenue > 0 ? `${formatMoney(revenue)} in bookings is ready for reconciliation.` : "That's okay. Tomorrow is another chance to help dancers grow.",
      to: "/founder-finance",
      action: "View finance",
      significance: revenue / 10
    },
    {
      id: "daily-story",
      icon: topStory?.icon ?? "✨",
      headline: topStory ? "Today's strongest signal is clear." : "You're just getting started.",
      insight: topStory?.text ?? "We'll highlight meaningful milestones as Chor grows.",
      to: "/story",
      action: "View timeline",
      significance: topStory?.significance ?? 0
    }
  ];

  return ranked.sort((left, right) => right.significance - left.significance).slice(0, 3);
}

function actionItems(docs: CachedBriefDocs): FounderBriefAction[] {
  const candidates = [
    {
      id: "stripe-verification",
      label: "Stripe verification pending.",
      detail: "A provider payout setup needs founder review.",
      value: numberValue(docs.platformHealth, ["stripeVerificationPending", "stripeVerificationIssues"]),
      status: "action-needed" as const,
      to: "/founder-finance"
    },
    {
      id: "failed-payouts",
      label: "Failed payout detected.",
      detail: "Review the payout queue before the next release run.",
      value: numberValue(docs.platformHealth, ["failedPayouts", "payoutFailures"]),
      status: "action-needed" as const,
      to: "/founder-finance"
    },
    {
      id: "media-backlog",
      label: "Media processing backlog.",
      detail: "Uploads are waiting longer than the healthy threshold.",
      value: numberValue(docs.platformHealth, ["mediaProcessingBacklog", "mediaBacklog"]),
      status: "watch" as const,
      to: "/system-health"
    },
    {
      id: "moderation",
      label: "Moderation report waiting.",
      detail: "A community safety item needs review.",
      value: numberValue(docs.platformHealth, ["moderationReports", "openModerationReports"]),
      status: "action-needed" as const,
      to: "/moderation"
    }
  ];

  return candidates.filter((action) => action.value > 0).slice(0, 4);
}

function explicitMilestoneReflections(docs: CachedBriefDocs): Array<FounderBriefReflection & { priority: number }> {
  const rawMilestones = [
    docs.userGrowth.founderMilestones,
    docs.userGrowth.reflectionMilestones,
    docs.platformHealth.founderMilestones
  ].flatMap((value) => (Array.isArray(value) ? value : []));

  return rawMilestones
    .filter((milestone): milestone is DocumentData => milestone && typeof milestone === "object" && !Array.isArray(milestone))
    .map((milestone, index) => {
      const text =
        stringField(milestone, "reflection") ??
        stringField(milestone, "founderReflection") ??
        stringField(milestone, "title") ??
        stringField(milestone, "headline") ??
        "";

      return {
        text: limitReflectionToTwoSentences(text),
        source: stringField(milestone, "source") ?? `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: founderMilestones`,
        activity: "milestone" as const,
        priority: numberValue(milestone, ["priority", "significance"], 500 - index)
      };
    })
    .filter((milestone) => milestone.text.length > 0);
}

function founderMilestoneReflection(docs: CachedBriefDocs): FounderBriefReflection | null {
  const milestoneReflections: Array<FounderBriefReflection & { priority: number }> = [
    ...explicitMilestoneReflections(docs),
    {
      text: "Today the first international dancer joined Chor.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: firstInternationalDancerJoinedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["firstInternationalDancerJoinedToday"]) ? 490 : 0
    },
    {
      text: "One year ago today, the first service was published.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: firstServicePublishedAnniversaryToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["firstServicePublishedAnniversaryToday"]) ? 480 : 0
    },
    {
      text: "The 1,000th booking was completed today.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: thousandthBookingCompletedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["thousandthBookingCompletedToday", "oneThousandthBookingCompletedToday"]) ? 470 : 0
    },
    {
      text: "The first studio outside Tennessee joined Chor.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: firstStudioOutsideTennesseeJoinedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["firstStudioOutsideTennesseeJoinedToday"]) ? 460 : 0
    },
    {
      text: "The first professional contract was posted.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: firstProfessionalContractPostedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["firstProfessionalContractPostedToday"]) ? 450 : 0
    },
    {
      text: "A dancer took their very first private lesson through Chor today.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: firstPrivateLessonCompletedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["firstPrivateLessonCompletedToday", "firstPrivateLessonBookedToday"]) ? 440 : 0
    },
    {
      text: "One coach reached their 100th completed lesson.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: coachHundredthCompletedLessonToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["coachHundredthCompletedLessonToday"]) ? 430 : 0
    },
    {
      text: "A studio completed another season of helping dancers grow.",
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: studioSeasonCompletedToday`,
      activity: "milestone",
      priority: booleanValue(docs.userGrowth, ["studioSeasonCompletedToday"]) ? 420 : 0
    }
  ];

  const selected = milestoneReflections.filter((reflection) => reflection.priority > 0).sort((left, right) => right.priority - left.priority)[0];
  if (!selected) return null;

  return {
    text: limitReflectionToTwoSentences(selected.text),
    source: selected.source,
    activity: selected.activity
  };
}

function founderReflection(docs: CachedBriefDocs): FounderBriefReflection {
  const milestoneReflection = founderMilestoneReflection(docs);
  if (milestoneReflection) return milestoneReflection;

  const completedLessons = numberValue(docs.userGrowth, ["completedLessonsToday", "lessonsCompletedToday", "bookingsCompletedToday"]);
  const bookedLessons = numberValue(docs.userGrowth, ["bookingsToday", "dailyBookings"]) || numberValue(docs.revenueToday, ["bookingsToday", "bookingCount"]);
  const servicesPublished = numberValue(docs.userGrowth, ["servicesPublishedToday", "servicesPublished", "newServicesToday"]);
  const opportunitiesPublished = numberValue(docs.userGrowth, ["opportunitiesPublishedToday", "opportunitiesPublished", "newOpportunitiesToday"]);
  const newDancers = numberValue(docs.userGrowth, ["newDancersToday", "dancersToday", "newDancers", "newUsersToday"]);
  const returningUsers = numberValue(docs.retention, ["returningUsersToday", "returningUsers", "activeReturningUsers"]);
  const newStudios = numberValue(docs.userGrowth, ["newStudiosThisWeek", "newStudiosToday", "studiosToday", "newStudios"]);
  const challengeSubmissions = numberValue(docs.userGrowth, ["challengeSubmissionsToday", "submissionsToday"]);

  const reflections: Array<FounderBriefReflection & { priority: number }> = [
    {
      text: `${plural(completedLessons, "lesson")} completed today.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: completedLessonsToday`,
      activity: "learning",
      priority: completedLessons > 0 ? 100 : 0
    },
    {
      text: `${plural(bookedLessons, "booking")} represented someone choosing to grow.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: bookingsToday`,
      activity: "learning",
      priority: bookedLessons > 0 ? 90 : 0
    },
    {
      text: `${plural(servicesPublished, "new service")} created more ways for dancers to train.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: servicesPublishedToday`,
      activity: "teaching",
      priority: servicesPublished > 0 ? 80 : 0
    },
    {
      text: `${plural(opportunitiesPublished, "opportunity")} opened new paths for dancers.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: opportunitiesPublishedToday`,
      activity: "opportunity",
      priority: opportunitiesPublished > 0 ? 75 : 0
    },
    {
      text: `${plural(returningUsers, "dancer")} returned to Chor today.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.retention}: returningUsersToday`,
      activity: "community",
      priority: returningUsers > 0 ? 70 : 0
    },
    {
      text: `${plural(newStudios, "studio")} joined Chor this week.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: newStudiosThisWeek`,
      activity: "community",
      priority: newStudios > 0 ? 65 : 0
    },
    {
      text: `${plural(challengeSubmissions, "Challenge submission")} carried someone's work forward.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: challengeSubmissionsToday`,
      activity: "community",
      priority: challengeSubmissions > 0 ? 60 : 0
    },
    {
      text: `${plural(newDancers, "new dancer")} found a place to begin.`,
      source: `${ADMIN_ANALYTICS_COLLECTION}/${ADMIN_ANALYTICS_DOCS.userGrowth}: newDancersToday`,
      activity: "growth",
      priority: newDancers > 0 ? 55 : 0
    }
  ];

  const selected = reflections.filter((reflection) => reflection.priority > 0).sort((left, right) => right.priority - left.priority)[0];
  if (selected) {
    return {
      text: limitReflectionToTwoSentences(selected.text),
      source: selected.source,
      activity: selected.activity
    };
  }

  return {
    text: "Today was quiet. Tomorrow brings another opportunity to help dancers grow.",
    source: "Founder Language quiet-day fallback",
    activity: "quiet"
  };
}

async function readCachedDoc(docId: string): Promise<DocumentData> {
  try {
    const snapshot = await getDoc(doc(db, ADMIN_ANALYTICS_COLLECTION, docId));
    return snapshot.exists() ? snapshot.data() : {};
  } catch (error) {
    logDevelopmentError(error);
    return {};
  }
}

export async function fetchFounderBrief(now = new Date()): Promise<FounderBrief> {
  const [userGrowth, retention, revenueToday, revenueMonth, platformHealth, coachEarnings] = await Promise.all([
    readCachedDoc(ADMIN_ANALYTICS_DOCS.userGrowth),
    readCachedDoc(ADMIN_ANALYTICS_DOCS.retention),
    readCachedDoc(ADMIN_ANALYTICS_DOCS.revenueToday),
    readCachedDoc(ADMIN_ANALYTICS_DOCS.revenueMonth),
    readCachedDoc(ADMIN_ANALYTICS_DOCS.platformHealthMetrics),
    readCachedDoc(ADMIN_ANALYTICS_DOCS.coachEarningsSummaries)
  ]);

  const docs = { userGrowth, retention, revenueToday, revenueMonth, platformHealth, coachEarnings };
  const stories = dailyStories(docs);
  const actions = actionItems(docs);
  const pulse = pulseFor(docs, stories);
  const timeline = cachedTimelineEvents(userGrowth).concat(cachedTimelineEvents(platformHealth)).slice(0, 6);

  return {
    greeting: founderGreeting(now),
    dateLabel: founderBriefDateLabel(now),
    pulse,
    stories,
    healthCards: healthCards(docs),
    insights: insights(docs, stories),
    actions,
    timeline,
    reflection: founderReflection(docs),
    isQuiet: stories.length === 0,
    lastUpdated: new Date()
  };
}
